import { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';

// ── Toolbox talks (admin) ───────────────────────────────────────────────────
// Post weekly safety talks; every active worker gets a notification and must
// sign off in their portal. The ack log below each talk is the WorkSafeBC-
// facing documentation (who acknowledged, when, and who still hasn't).

interface Ack {
  user_id: number; name: string; acked_at: string;
  worker_note?: string | null; approved_at?: string | null; approved_by_name?: string | null;
}
interface Talk {
  id: number; title: string; body: string; talk_date: string;
  category?: string; required?: number; due_date?: string | null;
  pinned?: number; archived?: number;
  created_by_name?: string; ack_count: number; acks: Ack[]; missing: string[];
}

const TEMPLATES: { label: string; title: string; body: string }[] = [
  { label: 'Ladder safety', title: 'Ladder safety', body: 'Inspect ladders before use — no bent rails, loose rungs, or missing feet. Maintain 3-point contact. Set extension ladders at 4:1 and tie off above 3 m. Never top-step a stepladder. Report damaged ladders and tag them out.' },
  { label: 'Falls — guardrails & openings', title: 'Fall protection: guardrails & floor openings', body: 'Guardrails go up before deck/stair openings are walked. Cover and label every floor opening — plywood screwed down, marked "HOLE". Harness + anchor above 3 m where guardrails are impractical. Check anchors before trusting them.' },
  { label: 'Power tool guards', title: 'Power tool guards & blades', body: 'Guards stay on — a pinned-back guard is a write-up. Unplug/lock out before blade changes. Sharp blades cut safer than dull ones. Eye and ear protection on for all cutting and nailing.' },
  { label: 'Housekeeping & materials', title: 'Site housekeeping & material handling', body: 'Clear walkways at all times — offcuts to the bin, cords overhead or along walls. Stack lumber flat and low. Bend at the knees; two-person lift over 50 lb or awkward sheet goods in wind. De-nail or bend over every nail in stripped material.' },
];

export const ToolboxTalksPage = () => {
  const [talks, setTalks] = useState<Talk[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [openTalk, setOpenTalk] = useState<number | null>(null);
  // CR #131 — posting options + views
  const [category, setCategory] = useState('safety');
  const [required, setRequired] = useState(true);
  const [dueDate, setDueDate] = useState('');
  const [pinned, setPinned] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');

  const load = () =>
    apiClient.get(`/safety/toolbox-talks${showArchived ? '?archived=1' : ''}`)
      .then(r => { setTalks(r.data.data ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showArchived]);

  // Office sign-off on a worker's acknowledgement
  const approveAck = async (talkId: number, userId: number, approved: boolean) => {
    setTalks(ts => ts.map(t => t.id !== talkId ? t : {
      ...t, acks: t.acks.map(a => a.user_id === userId ? { ...a, approved_at: approved ? new Date().toISOString() : null } : a),
    }));
    try { await apiClient.post(`/safety/toolbox-talks/${talkId}/acks/${userId}/approve`, { approved }); }
    catch { load(); }
  };
  const approveAll = async (talkId: number) => {
    try { await apiClient.post(`/safety/toolbox-talks/${talkId}/approve-all`); load(); } catch { /* ignore */ }
  };
  const patchTalk = async (id: number, body: Record<string, unknown>) => {
    setTalks(ts => ts.map(t => t.id === id ? { ...t, ...body } as Talk : t));
    try { await apiClient.patch(`/safety/toolbox-talks/${id}`, body); if ('archived' in body) load(); }
    catch { load(); }
  };

  const post = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await apiClient.post('/safety/toolbox-talks', {
        title: title.trim(), body: body.trim(), talk_date: date,
        category, required, due_date: dueDate || null, pinned,
      });
      setTitle(''); setBody(''); setDueDate(''); setPinned(false);
      load();
    } finally { setBusy(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this talk and its sign-off log?')) return;
    setTalks(ts => ts.filter(t => t.id !== id));
    try { await apiClient.delete(`/safety/toolbox-talks/${id}`); } catch { load(); }
  };

  return (
    <MainLayout>
      <LargeTitleHeader
        title="Toolbox Talks"
        subtitle="Weekly safety talks — workers sign off in their portal; the log satisfies WorkSafeBC documentation"
      />

      {/* Composer */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2.5">
        <div className="flex gap-1.5 flex-wrap">
          {TEMPLATES.map(t => (
            <button key={t.label} onClick={() => { setTitle(t.title); setBody(t.body); }}
              className="h-7 px-2.5 rounded-full text-[11px] font-semibold border border-gray-200 text-gray-600 hover:border-[#C77D3F]/50 hover:text-[#C77D3F]">
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Talk title"
            className="flex-1 min-w-[14rem] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
          <input type="date" value={date} onChange={e => setDate(e.target.value)} title="Talk date"
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none" />
        </div>
        <div className="flex gap-2 flex-wrap items-center text-xs">
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 h-9 bg-white text-gray-700">
            {['safety', 'quality', 'procedure', 'equipment'].map(c =>
              <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-gray-600" title="Every active worker must sign off">
            <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} className="accent-[#C77D3F]" />
            Sign-off required
          </label>
          <label className="flex items-center gap-1.5 text-gray-600">
            <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} className="accent-[#C77D3F]" />
            Pin to top
          </label>
          <label className="flex items-center gap-1.5 text-gray-600">
            Sign off by
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 h-9 text-gray-700" />
          </label>
        </div>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
          placeholder="The talk itself — what the crew needs to know and do this week"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />
        <button onClick={post} disabled={busy || !title.trim() || !body.trim()}
          className="h-10 px-4 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40 flex items-center gap-1.5">
          <span className="material-symbols-outlined text-base">campaign</span>
          {busy ? 'Posting…' : 'Post talk — notify all workers'}
        </button>
      </div>

      {/* View controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search talks…"
          className="h-9 px-3 rounded-lg border border-gray-200 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30" />
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="accent-[#C77D3F]" />
          Include archived
        </label>
        <span className="ml-auto text-xs text-gray-400">
          {talks.filter(t => t.required !== 0 && t.missing.length > 0).length} talk(s) awaiting crew sign-off
        </span>
      </div>

      {/* Talks + sign-off log */}
      {loading ? (
        <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
      ) : talks.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <span className="material-symbols-outlined text-gray-300 text-5xl block mb-2">health_and_safety</span>
          <p className="text-sm text-gray-500">No talks posted yet — use a template above to start the weekly habit.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {talks.filter(t => !search.trim() || (t.title + ' ' + t.body).toLowerCase().includes(search.trim().toLowerCase())).map(t => {
            const complete = t.missing.length === 0;
            const pendingApproval = t.acks.filter(a => !a.approved_at).length;
            return (
              <div key={t.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpenTalk(o => o === t.id ? null : t.id)}
                  className="w-full flex items-center gap-2.5 px-4 py-3 text-left">
                  <span className={`material-symbols-outlined text-xl ${complete ? 'text-green-600' : 'text-amber-500'}`}>
                    {complete ? 'verified' : 'pending_actions'}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 truncate">{t.title}</span>
                    <span className="block text-[11px] text-gray-400">
                      {new Date(t.talk_date + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {t.created_by_name ? ` · ${t.created_by_name}` : ''}
                      {t.category && t.category !== 'safety' ? ` · ${t.category}` : ''}
                      {t.due_date ? ` · due ${String(t.due_date).slice(5, 10)}` : ''}
                      {t.required === 0 ? ' · optional' : ''}
                      {t.archived ? ' · archived' : ''}
                    </span>
                  </span>
                  {!!t.pinned && <span className="material-symbols-outlined text-base text-[#C77D3F]" style={{ fontVariationSettings: "'FILL' 1" }}>push_pin</span>}
                  {pendingApproval > 0 && (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                      {pendingApproval} to approve
                    </span>
                  )}
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                    {t.ack_count} signed{t.missing.length ? ` · ${t.missing.length} missing` : ' — all in'}
                  </span>
                </button>
                {openTalk === t.id && (
                  <div className="border-t border-gray-100 px-4 py-3 space-y-2">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{t.body}</p>
                    <div className="text-xs">
                      <div className="flex items-center gap-2 mb-1.5">
                        <p className="font-bold text-gray-500 uppercase tracking-wide text-[10px]">Sign-off log &amp; office approval</p>
                        {pendingApproval > 0 && (
                          <button onClick={() => approveAll(t.id)}
                            className="h-6 px-2 rounded-md bg-[#C77D3F] text-white text-[10px] font-bold">
                            Approve all ({pendingApproval})
                          </button>
                        )}
                      </div>
                      {t.acks.length === 0 && <p className="text-gray-400 italic">Nobody has signed off yet.</p>}
                      {t.acks.map(a => (
                        <div key={a.user_id} className="flex items-start gap-2 py-1 border-b border-gray-50 last:border-0">
                          <span className="material-symbols-outlined text-sm text-green-600 mt-0.5">check_circle</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-gray-700 font-medium">{a.name}</span>
                            <span className="block text-gray-400">
                              signed {new Date(a.acked_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              {a.approved_at ? ` · approved by ${a.approved_by_name || 'office'}` : ''}
                            </span>
                            {a.worker_note && <span className="block text-gray-500 italic mt-0.5">“{a.worker_note}”</span>}
                          </span>
                          <button onClick={() => approveAck(t.id, a.user_id, !a.approved_at)}
                            title={a.approved_at ? 'Withdraw approval' : 'Approve this sign-off'}
                            className={`h-7 px-2.5 rounded-md text-[10px] font-bold border flex-shrink-0 ${
                              a.approved_at
                                ? 'bg-green-50 border-green-300 text-green-700'
                                : 'bg-white border-gray-300 text-gray-500 hover:border-[#C77D3F] hover:text-[#C77D3F]'
                            }`}>
                            {a.approved_at ? 'Approved' : 'Approve'}
                          </button>
                        </div>
                      ))}
                      {t.missing.length > 0 && (
                        <p className="text-amber-700 mt-1.5">Still missing: {t.missing.join(', ')}</p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap pt-1">
                      <button onClick={() => patchTalk(t.id, { pinned: !t.pinned })}
                        className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">
                        {t.pinned ? 'Unpin' : 'Pin to top'}
                      </button>
                      <button onClick={() => patchTalk(t.id, { archived: !t.archived })}
                        className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">
                        {t.archived ? 'Restore' : 'Archive'}
                      </button>
                      <button onClick={() => remove(t.id)}
                        className="h-8 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-bold ml-auto">Delete talk</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </MainLayout>
  );
};
